import React, {Component} from "react"
import PropTypes from "prop-types"
import {connect} from "react-redux"
import {translate} from "react-i18next"

import {vars} from "../../config"
import {CommentsCreators as Creators} from "./redux"
import {Container, Row, Col} from "../../components/Grid"
import CommentForm from "../../components/CommentForm"
import Button from "../../components/Button"
import Message from "../../components/Message"
import {checkEnv, dateToString} from "../../utils"

import styles from "./styles.css"

class Comments extends Component {
  static propTypes = {
    error: PropTypes.string,
    fetching: PropTypes.bool,
    comments: PropTypes.array,
    total_comments: PropTypes.number,
    className: PropTypes.string,
    id: PropTypes.string.isRequired,
    title: PropTypes.string
  }

  static defaultProps = {
    error: null,
    fetching: false,
    comments: [],
    total_comments: 0,
    className: "",
    title: null
  }

  state = {
    answerTo: null,
    answerName: ""
  }

  constructor(props) {
    super(props)

    this.commentform = null

    this.moreComments = this.moreComments.bind(this)
    this.addComment = this.addComment.bind(this)
    this.jumpToForm = this.jumpToForm.bind(this)
    this.answerToComment = this.answerToComment.bind(this)
    this.resetForm = this.resetForm.bind(this)
    this.commentRefs = {}
    this.lastHash = ""
  }

  componentDidUpdate() {
    const hash = this._getHash()

    if (hash && this.commentRefs[hash] && hash !== this.state.lastHash) {
      this._scrollToComment(hash)
    }
  }

  _getHash() {
    return this.props.router.hash === ""
      ? null
      : this.props.router.hash.replace("#", "")
  }

  _setCommentRef(name, elem) {
    this.commentRefs[name] = elem
  }

  _scrollToComment(hash) {
    const node = this.commentRefs[hash]

    if (node) {
      this.lastHash = hash

      // scroll to node
      setTimeout(() => {
        const bounding = node.getBoundingClientRect()
        const scrollValue = Math.floor(bounding.top) + window.scrollY - 220 // last value is safe space

        window.scrollTo(0, scrollValue)
      }, 350)
    }
  }

  addComment(message) {
    const {add, id} = this.props
    const {answerTo} = this.state

    // api call
    add(id, message, answerTo)

    // reset form
    this.resetForm()
  }

  moreComments() {
    const {request, id} = this.props

    request(id, vars.comments.per_page, 0)
  }

  padDate(i) {
    return ("00" + i).slice(-2)
  }

  getMonthName(m) {
    const months = [
      "Januar",
      "Februar",
      "März",
      "April",
      "Mai",
      "Juni",
      "Juli",
      "August",
      "September",
      "Oktober",
      "November",
      "Dezember"
    ]

    return months[m]
  }

  getDate(d) {
    const date = new Date(d.replace(" ", "T"))
    const day = date.getDate(),
      month = this.getMonthName(date.getMonth()),
      year = date.getFullYear(),
      hours = this.padDate(date.getHours()),
      minutes = this.padDate(date.getMinutes())

    return `${day}. ${month} ${year} um ${hours}:${minutes} Uhr`
  }

  answerToComment(comment) {
    this.setState(prevState => {
      return {
        ...prevState,
        answerTo: comment.comment_ID,
        answerName: comment.comment_author
      }
    })

    this.jumpToForm()
  }

  resetForm() {
    this.setState(prevState => {
      return {
        ...prevState,
        answerTo: null,
        answerName: ""
      }
    })
  }

  jumpToForm() {
    this.jumpToComment("comment_form")
  }

  jumpToComment(id) {
    const y =
      document.getElementById(id).getBoundingClientRect().top +
      window.pageYOffset -
      155

    window.scrollTo({top: y, behavior: "smooth"})
  }

  getTopLevelComments(comments) {
    return comments.filter(c => c.comment_parent === "0")
  }

  renderComment(comment) {
    const {lang, t} = this.props

    return (
      <div
        className={styles.Comment}
        id={`comment-${comment.comment_ID}`}
        key={`comment-${comment.comment_ID}`}
        ref={elem => this._setCommentRef(`comment-${comment.comment_ID}`, elem)}
      >
        <div className={styles.Meta}>
          <div className="avatar">
            <img
              src={comment.user_avatar}
              alt={`${t("Avatar von")} ${comment.comment_author}`}
            />
          </div>
          <div className="username">
            <strong>
              {comment.comment_author === null
                ? t("gelöschter Benutzer")
                : comment.comment_author}
            </strong>
            <p className="date has-icon">
              <i className="icon icon-calender" />
              <span>{dateToString(comment.comment_date, lang, "|")}</span>
              {this.renderAnswerHint(comment)}
            </p>
          </div>
        </div>
        <div
          className={styles.Message}
          dangerouslySetInnerHTML={{__html: comment.comment_content}}
        />
        {comment.level < 2 && (
          <div className="wrap-answer">
            <button
              onClick={() => this.answerToComment(comment)}
              className="answer"
            >
              {t("Antworten", {context: "comments"})}
            </button>
          </div>
        )}
        <div className={styles.CommentChildren}>
          {this.renderChildComments(comment._children)}
        </div>
      </div>
    )
  }

  renderChildComments(child_comments) {
    if (
      typeof child_comments === "undefined" ||
      Object.keys(child_comments).length === 0
    ) {
      return null
    }

    return Object.keys(child_comments).map(k => {
      return this.renderComment(child_comments[k])
    })
  }

  renderAnswerHint(comment) {
    if (comment.comment_parent === "0") return null
    const {comments, t} = this.props
    const parentIDString = Number(comment.comment_parent).toString()

    const parentCommentIndex = comments.findIndex(
      c => c.comment_ID === parentIDString
    )
    const parentComment = comments[parentCommentIndex]

    return (
      <span className="answer-hint">
        <button
          onClick={() =>
            this.jumpToComment(`comment-${parentComment.comment_ID}`)
          }
        >
          {t("Antwort auf {{author}}", {author: parentComment.comment_author})}
        </button>
      </span>
    )
  }

  render() {
    const {
      fetching,
      error,
      comments,
      total_comments,
      className,
      avatar,
      user_display_name,
      title,
      t
    } = this.props
    let classList = [styles.Wrapper]

    // if comments are disabled, return nothing
    if (checkEnv("DISABLE_COMMENTS")) return null

    if (className !== "") classList.push(className)
    if (fetching) classList.push("is-loading")
    if (error) classList.push("has-error")

    return (
      <div className={classList.join(" ")}>
        <Container>
          <Row>
            <Col>
              <h2 className="headline">{t("Kommentare")}</h2>
            </Col>
            <Col>
              <p
                className="info"
                dangerouslySetInnerHTML={{
                  __html: t("{{count}} Kommentare zu “{{title}}“", {
                    count: total_comments,
                    title
                  })
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              {comments.length === 0 && (
                <div className="no-comment">
                  {t("Keine Kommentare vorhanden")}
                </div>
              )}
              {this.getTopLevelComments(comments).map((comment, _index) =>
                this.renderComment(comment)
              )}
            </Col>
          </Row>
          {comments.length < total_comments && (
            <Row>
              <Col>
                <Button alignment="center" onClick={this.moreComments}>
                  "{t("Neue Kommentare laden")}"
                </Button>
              </Col>
            </Row>
          )}
          <Row>
            <Col>
              {error && (
                <Message error>
                  <p>
                    <strong>{t("Es ist ein Fehler aufgetreten.")}</strong>
                    <br />
                    {error}
                  </p>
                </Message>
              )}
            </Col>
          </Row>
          <Row>
            <Col>
              <div id="comment_form">
                <CommentForm
                  ref={commentform => (this.commentform = commentform)}
                  avatar={avatar}
                  username={user_display_name}
                  onSubmit={this.addComment}
                  answerTo={this.state.answerTo}
                  answerName={this.state.answerName}
                  resetForm={this.resetForm}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }
}

const mapStateToProps = state => {
  return {
    ...state.comments,
    avatar: state.login.avatar,
    user_display_name: state.login.user_display_name,
    lang: state.app.lang,
    router: state.router
  }
}

const mapDispatchToProps = dispatch => {
  return {
    request: (id, comments_per_page, offset) =>
      dispatch(Creators.request(id, comments_per_page, offset, true)),
    add: (id, message, parent) => dispatch(Creators.add(id, message, parent))
  }
}

// export default connect(mapStateToProps, mapDispatchToProps)(Comments)
Comments = connect(
  mapStateToProps,
  mapDispatchToProps
)(Comments)
export default translate()(Comments)

export {reducer} from "./redux"
export {default as sagas, add as addCommentSaga} from "./sagas"



// WEBPACK FOOTER //
// ./src/containers/Comments/index.js